import React, {useEffect, useMemo, useState} from 'react';
import {Outlet} from 'react-router-dom';

// material-ui
import {styled, Theme, useTheme} from '@mui/material/styles';
import {AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery} from '@mui/material';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import useConfig from 'hooks/useConfig';
import {drawerWidth} from 'store/constant';
import {openDrawer} from 'store/slices/menu';
import {useDispatch, useSelector} from 'store';

// assets
import {getAllArguments} from "../../store/actions/argument.actions";
import {getAllCompanies} from "../../store/actions/company.actions";
import {getAllTopics} from "../../store/actions/topic.actions";
import {getAllRooms} from "../../store/actions/room.actions";
import {getAllLocations} from "../../store/actions/location.actions";
import {getAllFirms} from "../../store/actions/firm.actions";
import {getLastConsolidate} from "../../store/actions/activites.actions";

interface MainStyleProps {
    theme: Theme;
    open: boolean;
}

// styles
const Main = styled('main', {shouldForwardProp: (prop) => prop !== 'open'})(({theme, open}: MainStyleProps) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));
    const {company, enableFullscreen, container, showCompanyHeader} = useConfig();

    const dispatch = useDispatch();
    const {drawerOpen} = useSelector((state) => state.menu);

    const {lastConsolidate} = useSelector((state) => state.activity);

    const [lastConsolidateLocal, setLastConsolidateLocal] = useState<any[]>([])
    const [enableFullscreenModal, setEnableFullscreenModal] = useState<boolean | undefined>(false)

    useEffect(() => {
        setLastConsolidateLocal(lastConsolidate)
    }, [lastConsolidate])

    useEffect(() => {
        const init = async () => {
            await dispatch(getLastConsolidate(company));
        }
        init()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    useEffect(() => {
        setEnableFullscreenModal(enableFullscreen)
    }, [enableFullscreen]);

    useEffect(() => {
        const init = async () => {
            await dispatch(getAllArguments());
            await dispatch(getAllCompanies());
            await dispatch(getAllTopics());
            await dispatch(getAllRooms());
            await dispatch(getAllLocations());
            await dispatch(getAllFirms());
            await dispatch(getLastConsolidate(company));
        }
        init()
        dispatch(openDrawer(!matchDownMd));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const header = useMemo(
        () => (
            <Toolbar>
                <Header showCompanyField={showCompanyHeader}/>
            </Toolbar>
        ),
        []
    );

    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                {header}
            </AppBar>

            {/* drawer */}
            <Sidebar/>

            {/* main content */}
            <Main theme={theme} open={drawerOpen}>
                {/* breadcrumb */}
                {container && (
                    <Container maxWidth="lg">
                        <Outlet/>
                    </Container>
                )}
                {!container && (
                    <>
                        <Outlet/>
                    </>
                )}
            </Main>
        </Box>
    );
};

/*
<Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign
                                     showCompanyField={showCompanyHeader} companiesArray={companiesArray}
                                     lastConsolidate={lastConsolidateLocal}/>
 */

export default MainLayout;